/**
 * Shared helpers for the mobile image gallery experience.
 * Keeps data normalization consistent across gallery instances.
 */

import placeholderImage from '../../../assets/images/placeholder-post-image.png';

export const DEFAULT_GALLERY_PAGE_SIZE = 50;

export const mapSubtypeToGalleryType = (subtype) => {
  if (!subtype) {
    return 'uploaded';
  }

  const normalized = String(subtype).toLowerCase();
  if (normalized === 'other') {
    return 'edited';
  }

  if (['uploaded', 'generated', 'edited'].includes(normalized)) {
    return normalized;
  }

  return 'uploaded';
};

export const getSubtypeQueryValue = (type) => {
  if (!type || type === 'all') {
    return null;
  }

  if (type === 'edited') {
    return 'other';
  }

  return type;
};

const getTranslationFallback = (t, key, fallback) => {
  if (typeof t !== 'function') {
    return fallback;
  }

  const translation = t(key);
  if (!translation || translation === key) {
    return fallback;
  }

  return translation;
};

export const normalizeMediaItem = (item, t) => {
  if (!item) {
    return null;
  }

  const id = item.id || item._id || item.media_id || item.mediaId || item.url || item.public_url;
  if (!id) {
    return null;
  }

  const resolvedMediaType = (() => {
    const candidates = [
      item.media_type,
      item.mediaType,
      item.type,
      item.metadata?.media_type,
      item.metadata?.type,
    ];
    const match = candidates
      .map((value) => (value ? String(value).toLowerCase() : ''))
      .find((value) => value === 'image' || value === 'video');
    return match || 'image';
  })();
  const isVideo = resolvedMediaType === 'video';

  const urlCandidate = item.public_url || item.url || item.file_url || item.fileUrl || null;
  const url = urlCandidate || (isVideo ? null : placeholderImage);
  const nameFallbackKey = isVideo ? 'imageGallery.items.untitledVideo' : 'imageGallery.items.untitled';
  const nameFallbackValue = isVideo ? 'Video' : 'Media senza nome';
  const name = item.name || item.title || item.filename || getTranslationFallback(t, nameFallbackKey, nameFallbackValue);
  const uploadedAt = item.created_at || item.createdAt || item.uploaded_at || item.uploadedAt || null;
  const galleryType = mapSubtypeToGalleryType(
    item.subtype || item.metadata?.subtype || item.source || item.category
  );

  return {
    id,
    url,
    name,
    uploadedAt,
    type: galleryType,
    mediaType: resolvedMediaType,
    raw: item,
  };
};

const collectMediaDetailsByIds = async ({ get, ids }) => {
  if (!Array.isArray(ids) || !ids.length) {
    return [];
  }

  const detailPromises = ids.map((mediaId) => {
    return get(`/api/v1/media/files/${mediaId}`)
      .then((detail) => detail?.media_file || detail?.media || detail)
      .catch((error) => {
        console.error('[galleryUtils] Failed to load media detail', { mediaId, error });
        return null;
      });
  });

  const details = await Promise.all(detailPromises);
  return details.filter(Boolean);
};

const extractMediaListFromPayload = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload?.media_files)) {
    return payload.media_files;
  }

  if (Array.isArray(payload?.mediaFiles)) {
    return payload.mediaFiles;
  }

  return [];
};

export const fetchAlbumMediaItems = async ({ get, albumId }) => {
  if (!albumId) {
    return [];
  }

  const albumResponse = await get(`/api/v1/media/albums/${albumId}`);
  const albumPayload = albumResponse?.album || albumResponse?.data?.album || albumResponse;
  if (!albumPayload) {
    return [];
  }

  let mediaList = extractMediaListFromPayload(albumPayload);

  if (!mediaList.length && Array.isArray(albumPayload?.media_file_ids)) {
    mediaList = await collectMediaDetailsByIds({
      get,
      ids: albumPayload.media_file_ids,
    });
  }

  if (!mediaList.length && Array.isArray(albumPayload?.mediaFileIds)) {
    mediaList = await collectMediaDetailsByIds({
      get,
      ids: albumPayload.mediaFileIds,
    });
  }

  return mediaList;
};

export const generateVideoThumbnail = (videoUrl, options = {}) => new Promise((resolve, reject) => {
  if (!videoUrl || typeof document === 'undefined') {
    resolve(null);
    return;
  }

  const { width = 240, quality = 0.8, seekTime = 0.1 } = options || {};
  const video = document.createElement('video');
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    resolve(null);
    return;
  }

  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'metadata';

  const cleanup = () => {
    video.removeEventListener('loadeddata', handleLoadedData);
    video.removeEventListener('error', handleError);
    video.src = '';
    video.load();
  };

  const handleLoadedData = () => {
    try {
      const videoWidth = video.videoWidth || 0;
      const videoHeight = video.videoHeight || 0;
      const aspectRatio = videoWidth && videoHeight ? videoWidth / videoHeight : 1;
      canvas.width = width;
      canvas.height = width / Math.max(aspectRatio, 0.1);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const thumbnailDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(thumbnailDataUrl);
    } catch (error) {
      reject(error);
    } finally {
      cleanup();
    }
  };

  const handleError = () => {
    cleanup();
    reject(new Error('Failed to load video for thumbnail generation'));
  };

  video.addEventListener('loadeddata', handleLoadedData);
  video.addEventListener('error', handleError);
  video.src = videoUrl;
  try {
    video.currentTime = seekTime;
  } catch (error) {
    // Ignore seek errors; loadeddata will still attempt a frame.
  }
});
